import { useCallback, useId, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import NotFoundPage from "components/error/NotFoundPage";
import { SavedFilterView, SortStateDescriptor } from "components/Filters/types";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import useErrorHandle from "hooks/useErrorHandle";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";

import { ANALYTICS, MODULES_LIMIT, MODULES_LIMIT_STORAGE_KEY } from "./constants";
import FiltersLayout from "./FiltersLayout";
import useModules from "./useModules";
import ModulesPageLayout from "./PageLayout";
import ModulesTable from "./Table";
import ModulesEmpty from "./Empty";
import useModulesAnalytics from "./useModulesAnalytics";
import useModulesStore from "./useModulesStore";

const ModulesNew = () => {
  const [currentSavedView, setCurrentSavedView] = useState<SavedFilterView | undefined>(undefined);

  const { allItemsQty } = useModulesStore(
    useShallow((state) => ({
      allItemsQty: state.allItemsQty,
    }))
  );

  const {
    initialLoading,
    error,
    modules,
    loadModules,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    searchIsEmpty,
    paginationResetKey,
  } = useModules();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    ...ANALYTICS,
    fetchItems: loadModules,
    endCursor,
    startCursor,
    allItemsCount: allItemsQty,
    visibleItemsCount: modules.length,
    initialLimit: MODULES_LIMIT,
    storageKey: MODULES_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const trackAnalytics = useModulesAnalytics();

  const handleSortCallback = useCallback(
    (descriptor: SortStateDescriptor) => {
      trackAnalytics("Sort by column", { ...descriptor });
    },
    [trackAnalytics]
  );

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <ModulesPageLayout hideActions>
        <PageLayoutSkeleton />
      </ModulesPageLayout>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  return (
    <ModulesPageLayout>
      <FiltersLayout
        currentSavedView={currentSavedView}
        setCurrentSavedView={setCurrentSavedView}
        titleId={titleId}
      >
        {isEmpty && <ModulesEmpty hasNoSearchResult={searchIsEmpty} />}

        {!isEmpty && (
          <>
            <ModulesTable
              modules={modules}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={allItemsQty}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </FiltersLayout>
    </ModulesPageLayout>
  );
};

export default ModulesNew;
